// Position-keyed opening theory graph generated from the pinned CC0
// lichess-org/chess-openings corpus. The graph is lazy-loaded: ordinary play
// does not pay for the Study database chunk.

export type BookMoveRole = "primary" | "alternative";

export interface BookOpening {
  eco: string;
  name: string;
}

export interface BookMove {
  uci: string;
  san: string;
  /** Number of named corpus lines sharing this edge, not game frequency. */
  theoryLines: number;
  role: BookMoveRole;
  resultingOpening?: BookOpening;
}

export interface BookPosition {
  epd: string;
  opening?: BookOpening;
  moves: BookMove[];
}

export interface OpeningBookSource {
  id: string;
  revision: string;
  license: string;
  url: string;
}

interface RawPosition {
  o?: string;
  m: ([string, string, number] | [string, string, number, string])[];
}

export interface RawOpeningBook {
  version: number;
  source: OpeningBookSource;
  maxPly: number;
  maxMovesPerPosition: number;
  positions: Record<string, RawPosition>;
}

let cache: RawOpeningBook | null = null;
let loading: Promise<RawOpeningBook> | null = null;

/** First four FEN fields: the normalized position key used by the repository. */
export function openingEpd(fen: string): string {
  return fen.split(" ").slice(0, 4).join(" ");
}

function parseOpening(value: string | undefined): BookOpening | undefined {
  if (!value) return undefined;
  const tab = value.indexOf("\t");
  if (tab < 1 || tab === value.length - 1) return undefined;
  return { eco: value.slice(0, tab), name: value.slice(tab + 1) };
}

/** Pure lookup used by the UI and repository validation tests. */
export function findBookPosition(fen: string, book: RawOpeningBook): BookPosition | null {
  const epd = openingEpd(fen);
  const raw = book.positions[epd];
  if (!raw) return null;
  return {
    epd,
    opening: parseOpening(raw.o),
    moves: raw.m.map((move, index) => ({
      uci: move[0],
      san: move[1],
      theoryLines: move[2],
      role: index === 0 ? "primary" : "alternative",
      resultingOpening: parseOpening(move[3]),
    })),
  };
}

export async function loadOpeningBook(): Promise<RawOpeningBook> {
  if (cache) return cache;
  if (!loading) {
    loading = import("../data/openingBook.json").then((module) => {
      const imported = module as unknown as { default?: RawOpeningBook };
      cache = (imported.default ?? (module as unknown)) as RawOpeningBook;
      return cache;
    });
    loading.catch(() => {
      loading = null;
    });
  }
  return loading;
}

/** Synchronous lookup after preload; useful for classifying a dragged move. */
export function cachedBookPosition(fen: string): BookPosition | null {
  return cache ? findBookPosition(fen, cache) : null;
}

/** Lazy lookup; failures degrade to no theory suggestions. */
export async function lookupBookPosition(fen: string): Promise<BookPosition | null> {
  try {
    return findBookPosition(fen, await loadOpeningBook());
  } catch {
    return null;
  }
}

export function openingBookSource(): OpeningBookSource | null {
  return cache?.source ?? null;
}
